/**
 * HorizontalScrollbar
 * ───────────────────
 * A premium, obviously-grabbable "drawer-pull" scrollbar that sits BELOW a
 * horizontally-scrolling strip whose native scrollbar is hidden (see
 * `.storiz-strip-no-native-bar` in index.css).
 *
 * Design intent
 *   The page filmstrip can hold 100+ spreads, so the control needs to READ as
 *   draggable at a glance. The thumb is a rounded gold pill carrying a 3-line
 *   grip — the same signal a physical drawer pull or slider knob gives. It
 *   stays visible whenever the strip overflows (no auto-hide guessing games),
 *   and lifts + brightens while dragging so the finger has clear feedback.
 *
 * Behaviour
 *   • Thumb width is proportional to viewport / content, with a comfortable
 *     minimum touch target.
 *   • Drag the thumb → strip scrolls under the finger, 1:1.
 *   • Click the track → smooth-scrolls so the thumb lands under the click.
 *   • Hides only when everything fits (nothing to scroll).
 *
 * Native scroll (wheel, touch swipe) still works — this is an enhancement.
 * Lives outside the dnd-kit SortableContext, so reorder drags never reach it.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

type Props = {
  scrollRef: React.RefObject<HTMLDivElement | null>;
};

// Brand gold, lit from the top like a metal pull.
const GOLD_TOP = '#D9B65E';
const GOLD_BOTTOM = '#C4972F';
const TRACK_GROOVE = 'rgba(201,162,74,0.14)';
const GRIP = 'rgba(255,255,255,0.6)';
const MIN_THUMB_PX = 44;   // comfortable touch target even with 100+ pages
const TRACK_INSET = 10;    // left/right padding so the pill never touches edges

export default function HorizontalScrollbar({ scrollRef }: Props) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const thumbRef = useRef<HTMLDivElement | null>(null);

  const [scrollable, setScrollable] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [hover, setHover] = useState(false);
  const [thumb, setThumb] = useState({ width: 0, left: 0 });

  // Drag state lives in a ref to avoid re-renders mid-drag.
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startScrollLeft: number;
  } | null>(null);

  // ── Recompute thumb geometry ────────────────────────────────────────
  const recompute = () => {
    const strip = scrollRef.current;
    const track = trackRef.current;
    if (!strip || !track) return;

    const { scrollWidth, clientWidth, scrollLeft } = strip;
    const trackWidth = track.clientWidth - TRACK_INSET * 2;

    if (scrollWidth <= clientWidth + 1 || trackWidth <= 0) {
      setScrollable(false);
      return;
    }
    setScrollable(true);

    const ratio = clientWidth / scrollWidth; // 0..1
    const width = Math.max(MIN_THUMB_PX, Math.min(trackWidth, ratio * trackWidth));
    const travel = Math.max(0, trackWidth - width);
    const maxScroll = Math.max(1, scrollWidth - clientWidth);
    const left = (scrollLeft / maxScroll) * travel;

    setThumb({ width, left });
  };

  // ── Wire up resize + scroll observers ───────────────────────────────
  useLayoutEffect(() => {
    const strip = scrollRef.current;
    if (!strip) return;

    recompute();

    let contentRO: ResizeObserver | null = null;
    const stripRO = new ResizeObserver(recompute);
    stripRO.observe(strip);
    const content = strip.firstElementChild as HTMLElement | null;
    if (content) {
      contentRO = new ResizeObserver(recompute);
      contentRO.observe(content);
    }

    const onScroll = () => recompute();
    strip.addEventListener('scroll', onScroll, { passive: true });
    const onWindowResize = () => recompute();
    window.addEventListener('resize', onWindowResize);

    return () => {
      stripRO.disconnect();
      contentRO?.disconnect();
      strip.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onWindowResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollRef]);

  // ── Thumb drag ──────────────────────────────────────────────────────
  const onThumbPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const strip = scrollRef.current;
    if (!strip) return;
    e.preventDefault();
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startScrollLeft: strip.scrollLeft,
    };
    setDragging(true);
  };

  const onThumbPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const strip = scrollRef.current;
    const track = trackRef.current;
    if (!drag || !strip || !track) return;
    if (e.pointerId !== drag.pointerId) return;

    const dx = e.clientX - drag.startX;
    const trackWidth = track.clientWidth - TRACK_INSET * 2;
    const travel = Math.max(1, trackWidth - thumb.width);
    const maxScroll = Math.max(1, strip.scrollWidth - strip.clientWidth);
    const next = drag.startScrollLeft + (dx / travel) * maxScroll;
    strip.scrollLeft = Math.max(0, Math.min(maxScroll, next)); // 1:1, no smoothing
  };

  const onThumbPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || e.pointerId !== drag.pointerId) return;
    try { (e.target as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    dragRef.current = null;
    setDragging(false);
  };

  // ── Track click → smooth-scroll to that fraction ────────────────────
  const onTrackPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.target === thumbRef.current) return; // thumb has its own handler
    const strip = scrollRef.current;
    const track = trackRef.current;
    if (!strip || !track) return;
    const rect = track.getBoundingClientRect();
    const clickX = e.clientX - rect.left - TRACK_INSET;
    const trackWidth = rect.width - TRACK_INSET * 2;
    const targetLeft = Math.max(0, Math.min(trackWidth - thumb.width, clickX - thumb.width / 2));
    const travel = Math.max(1, trackWidth - thumb.width);
    const fraction = targetLeft / travel;
    const maxScroll = Math.max(1, strip.scrollWidth - strip.clientWidth);
    strip.scrollTo({ left: fraction * maxScroll, behavior: 'smooth' });
  };

  useEffect(() => {
    recompute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollable]);

  if (!scrollable) return null;

  const active = dragging || hover;
  const thumbHeight = active ? 12 : 10;
  const showGrip = thumb.width >= 30;

  return (
    <div
      ref={trackRef}
      onPointerDown={onTrackPointerDown}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { if (!dragRef.current) setHover(false); }}
      aria-hidden="true"
      style={{
        position: 'relative',
        width: '100%',
        height: 20,               // generous hit area
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        touchAction: 'none',      // don't leak gestures to dnd-kit above
        userSelect: 'none',
      }}
    >
      {/* Track groove */}
      <div
        style={{
          position: 'absolute',
          left: TRACK_INSET,
          right: TRACK_INSET,
          height: 6,
          borderRadius: 3,
          background: TRACK_GROOVE,
          boxShadow: 'inset 0 1px 1px rgba(0,0,0,0.04)',
        }}
      />
      {/* Draggable pull handle */}
      <div
        ref={thumbRef}
        onPointerDown={onThumbPointerDown}
        onPointerMove={onThumbPointerMove}
        onPointerUp={onThumbPointerUp}
        onPointerCancel={onThumbPointerUp}
        style={{
          position: 'absolute',
          left: TRACK_INSET + thumb.left,
          width: thumb.width,
          height: thumbHeight,
          borderRadius: thumbHeight / 2,
          background: `linear-gradient(180deg, ${GOLD_TOP} 0%, ${GOLD_BOTTOM} 100%)`,
          boxShadow: active
            ? '0 2px 6px rgba(196,151,47,0.45), inset 0 1px 0 rgba(255,255,255,0.5)'
            : '0 1px 3px rgba(196,151,47,0.35), inset 0 1px 0 rgba(255,255,255,0.4)',
          cursor: dragging ? 'grabbing' : 'grab',
          transition: 'height 150ms ease, box-shadow 150ms ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          touchAction: 'none',
        }}
      >
        {/* Grip — the "you can drag me" affordance */}
        {showGrip && [0, 1, 2].map((i) => (
          <span
            key={i}
            style={{
              width: 1.5,
              height: active ? 6 : 5,
              borderRadius: 1,
              background: GRIP,
              transition: 'height 150ms ease',
            }}
          />
        ))}
      </div>
    </div>
  );
}
